import { Check } from 'lucide-react';
import type { ReactElement } from 'react';
import { cn } from '@/shared/ui';
import { SETUP_STEPS } from '../model/setup-steps';

interface ProductSetupStepsProps {
  /** 단계별 완료 여부(SETUP_STEPS 순서). 지금 단계는 아직 안 채운 첫 단계다. */
  readonly done: readonly boolean[];
  /** true 면 단계 이름이 수정 화면의 그 섹션으로 가는 링크가 된다. */
  readonly linkSections?: boolean;
}

/**
 * 상품 등록·수정 오른쪽 안내 — 공개까지의 순서, 채운 단계(✓)와 지금 단계, 원통 상품 팁.
 * 수정 화면에서는 단계를 눌러 해당 섹션으로 이동한다.
 */
export const ProductSetupSteps = ({
  done,
  linkSections = false,
}: ProductSetupStepsProps): ReactElement => {
  const current = done.indexOf(false);

  return (
    <aside className="flex flex-col gap-4 lg:sticky lg:top-0">
      <div className="rounded-2xl border border-foreground/[0.06] bg-card p-6 shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
        <p className="text-sm font-semibold">등록 순서</p>
        <ol aria-label="등록 순서" className="mt-5 flex flex-col">
          {SETUP_STEPS.map((step, index) => {
            const isCurrent = index === current;
            const isDone = done[index] === true;
            const title = (
              <span
                className={cn(
                  'text-sm',
                  isCurrent ? 'font-semibold text-foreground' : 'text-foreground/70',
                )}
              >
                {step.title}
              </span>
            );

            return (
              <li
                key={step.key}
                aria-current={isCurrent ? 'step' : undefined}
                className="relative flex gap-3 pb-5 last:pb-0"
              >
                {/* 단계 사이 세로선 — 마지막 단계에는 없다. 채운 단계 아래는 라임. */}
                {index < SETUP_STEPS.length - 1 ? (
                  <span
                    aria-hidden
                    className={cn(
                      'absolute top-7 bottom-1 left-[13px] w-px',
                      isDone ? 'bg-brand-lime' : 'bg-foreground/10',
                    )}
                  />
                ) : null}
                <span
                  aria-hidden
                  className={cn(
                    'relative flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                    isCurrent && 'bg-primary text-brand-lime',
                    isDone && 'bg-brand-lime text-black',
                    !isCurrent && !isDone && 'bg-foreground/[0.06] text-foreground/50',
                  )}
                >
                  {isDone ? <Check className="size-3.5" strokeWidth={3} /> : index + 1}
                </span>
                <span className="flex min-w-0 flex-col pt-0.5">
                  {linkSections ? (
                    <a
                      href={`#${step.anchorId}`}
                      className="underline-offset-4 hover:underline focus-visible:underline focus-visible:outline-none"
                    >
                      {title}
                    </a>
                  ) : (
                    title
                  )}
                  <span className="mt-0.5 text-xs text-muted-foreground">
                    {step.note}
                    {isDone ? <span className="sr-only"> (완료)</span> : null}
                  </span>
                </span>
              </li>
            );
          })}
        </ol>
      </div>

      <div className="rounded-2xl bg-primary p-5 text-sm text-white/80">
        <p className="font-semibold text-brand-lime">원통 상품이라면</p>
        <p className="mt-2 leading-relaxed">
          머그·텀블러처럼 둥근 상품은 실측 종류를 <b className="text-white">원통</b>으로 고르고
          지름을 넣으세요. 편집영역을 저장한 뒤에는 바꿀 수 없습니다.
        </p>
      </div>
    </aside>
  );
};
